import React from "react";
import { Box, BoxProps, Button, Flex, Image, Link, Text, TextProps } from "@chakra-ui/react";
import { landing } from "../landingTokens";
import { useLandingTheme } from "../LandingThemeContext";
import { useLandingCopy } from "../LandingLanguageContext";
import { trackLandingCallToActionClick } from "../handoff/appHandoff";
import { AppStore, AppStorePlacement, readAppStoreRouteFromDocument, trackAppStoreClick } from "../handoff/appStoreRoute";
import { useAppStoreUrl, useAppUrl } from "../handoff/useAppUrl";
import { WEB_APP_CTA_CLASS_NAME } from "./AppStoreCallToAction";

function scaledPixels(basePixels: number, scale: number) {
  return `${Math.round(basePixels * scale)}px`;
}

export function SectionHeading({ children, ...props }: TextProps) {
  const theme = useLandingTheme();
  const scale = theme.heading.sizeScale;
  return (
    <Text
      as="h2"
      fontFamily={theme.font.display}
      fontWeight={theme.heading.weight}
      letterSpacing={theme.heading.letterSpacing}
      textTransform={theme.heading.transform}
      fontSize={{ base: scaledPixels(24, scale), md: scaledPixels(32, scale), lg: scaledPixels(37, scale) }}
      lineHeight={1.2}
      color={theme.color.heading}
      textAlign="center"
      {...props}
    >
      {children}
    </Text>
  );
}

// Small tracked label used above headings by the themed versions.
export function Eyebrow({ children, ...props }: TextProps) {
  const theme = useLandingTheme();
  return (
    <Text
      fontFamily={theme.font.mono}
      fontSize="12px"
      fontWeight={600}
      letterSpacing="0.14em"
      textTransform="uppercase"
      color={theme.color.accent}
      {...props}
    >
      {children}
    </Text>
  );
}

export function TryItFreeButton() {
  const theme = useLandingTheme();
  const callsToActionCopy = useLandingCopy().callsToAction;
  const appHomeHref = useAppUrl("/");
  return (
    <Button
      as="a"
      href={appHomeHref}
      onClick={() => trackLandingCallToActionClick("try_it_free")}
      bg={theme.color.accent}
      color={theme.button.primaryColor}
      border={theme.button.border}
      boxShadow={theme.button.shadow}
      fontFamily={theme.font.body}
      fontSize="20px"
      fontWeight={600}
      h="64px"
      px="64px"
      borderRadius={theme.shape.buttonRadius === "12px" ? "full" : theme.shape.buttonRadius}
      transition={landing.buttonHoverTransition}
      _hover={{ bg: theme.color.accentHover, transform: theme.button.hoverTransform, textDecoration: "none" }}
      _focusVisible={{ boxShadow: `0 0 0 3px ${theme.color.highlight}` }}
    >
      {callsToActionCopy.tryItFreeButton}
    </Button>
  );
}

// Every phone narrower than 768px sees these badges, so only a routed visitor's tap (a phone visitor from a Meta
// ad, see handoff/appStoreRoute.ts) is reported as a store click. Anyone else's stays untracked, as before
// routing existed: "get_app" means a store call to action offered to a Meta-ad visitor, and a tap must not
// start sending Google Ads / TikTok conversions for organic phone traffic. Read at click time, never in render.
function trackRoutedAppStoreBadgeClick(store: AppStore, placement: AppStorePlacement) {
  if (readAppStoreRouteFromDocument() === null) return;
  trackAppStoreClick(store, placement);
}

function AppStoreBadge({ store, placement }: { store: AppStore; placement: AppStorePlacement }) {
  const callsToActionCopy = useLandingCopy().callsToAction;
  const href = useAppStoreUrl(store);
  const isAppStore = store === "ios";
  return (
    // Same tab: phones hand store links to the store app. The class lets a routed visitor see only their store.
    <Link href={href} className={`app-store-badge--${store}`} onClick={() => trackRoutedAppStoreBadgeClick(store, placement)}>
      <Image
        src={isAppStore ? "/images/landing/app-store-badge.png" : "/images/landing/google-play-badge.png"}
        alt={isAppStore ? callsToActionCopy.appStoreBadgeAlt : callsToActionCopy.googlePlayBadgeAlt}
        // Lazy: a hidden badge (desktop, or the other store for a routed visitor) is never downloaded.
        loading="lazy"
        w="100%"
      />
    </Link>
  );
}

// On phones Framer swaps the "try it free" button for App Store / Google Play badges. Phone visitors from a
// Meta ad (see handoff/appStoreRoute.ts) see them at any width, and only their own store's badge.
export function MobileAppStoreBadges({ placement = "section_badges", ...props }: BoxProps & { placement?: AppStorePlacement }) {
  return (
    <Flex className="app-store-badges" direction="column" gap="20px" w="100%" maxW="351px" mx="auto" {...props}>
      <AppStoreBadge store="ios" placement={placement} />
      <AppStoreBadge store="android" placement={placement} />
    </Flex>
  );
}

export function PrimaryCallToAction(props: BoxProps) {
  return (
    <Box {...props}>
      <Flex className={WEB_APP_CTA_CLASS_NAME} display={{ base: "none", md: "flex" }} justify="center">
        <TryItFreeButton />
      </Flex>
      <MobileAppStoreBadges display={{ base: "flex", md: "none" }} />
    </Box>
  );
}

export function ContentContainer({ children, maxW = landing.contentWidth, ...props }: BoxProps) {
  return (
    <Box w="100%" maxW={maxW} mx="auto" px={landing.contentSidePadding} {...props}>
      {children}
    </Box>
  );
}

// A surface styled by the active theme (border, shadow, radius) — cards, panels, media frames.
export function ThemedCard({ children, ...props }: BoxProps) {
  const theme = useLandingTheme();
  return (
    <Box
      bg={theme.color.surface}
      border={theme.card.border}
      boxShadow={theme.card.shadow}
      borderRadius={theme.shape.cardRadius}
      {...props}
    >
      {children}
    </Box>
  );
}
